import { useIsBrandedIconLibraryEnabled } from '@/app-shell/flags/branded-icon-library-flag';
import {
  useIsExperimentalDesignSystemButtonsEnabled,
  useIsExperimentalDesignSystemFormsEnabled,
} from '@/app-shell/flags/shell-flags';
import { trackErrorViewed } from '@/utils/analytics/trackErrorViewed';
import { ErrorSeverity } from '@heidi/analytics/events';
import {
  ExperimentalDesignSystemContextProvider,
  ToastAnalyticsContextProvider,
  type ToastAnalyticsContextValue,
} from '@heidi/design-system/contexts';
import { ErrorSurface } from '@heidi/templates/contracts';
import { useCallback, useMemo, type ReactNode } from 'react';

interface DesignSystemProviderProps {
  children: ReactNode;
  /** Override for testing/Storybook. When provided, skips the feature-flag check. */
  isExperimentalForms?: boolean;
  /** Override for testing/Storybook. When provided, skips the feature-flag check. */
  isExperimentalButtons?: boolean;
  /** Override for testing/Storybook. When provided, skips the feature-flag check. */
  isBrandedIcons?: boolean;
}

export function DesignSystemProvider({
  children,
  isExperimentalForms: isExperimentalFormsOverride,
  isExperimentalButtons: isExperimentalButtonsOverride,
  isBrandedIcons: isBrandedIconsOverride,
}: DesignSystemProviderProps) {
  // DS-1369 — both experimental design-system gates are Flagsmith flags read via
  // reactive `useBooleanFlagValue` hooks; they re-render on their own when flags
  // load, so no readiness gating is required. The button colour rework value is
  // exposed raw on the context; `Button` only applies it when forms is on.
  const isExperimentalFormsFlag = useIsExperimentalDesignSystemFormsEnabled();
  const isExperimentalButtonsFlag = useIsExperimentalDesignSystemButtonsEnabled();
  const isBrandedIconsFlag = useIsBrandedIconLibraryEnabled();

  const isExperimentalForms = isExperimentalFormsOverride ?? isExperimentalFormsFlag;

  const isExperimentalButtons =
    isExperimentalButtonsOverride ?? isExperimentalButtonsFlag;

  const isBrandedIcons = isBrandedIconsOverride ?? isBrandedIconsFlag;

  const value = useMemo(
    () => ({ isExperimentalForms, isExperimentalButtons, isBrandedIcons }),
    [isExperimentalForms, isExperimentalButtons, isBrandedIcons]
  );

  // ARCFE-787: toast now lives in the (analytics-agnostic) design-system
  // package and reports error-class toasts through this injected callback. The
  // `tone → severity` + `TOAST` surface mapping stays app-side, where analytics
  // belongs — the package never imports `trackErrorViewed`.
  const onErrorToast = useCallback<
    NonNullable<ToastAnalyticsContextValue['onErrorToast']>
  >(
    ({ message, tone }) =>
      trackErrorViewed({
        surface: ErrorSurface.TOAST,
        error_message: message,
        severity: tone === 'destructive' ? ErrorSeverity.CRITICAL : ErrorSeverity.WARNING,
      }),
    []
  );

  const toastAnalyticsValue = useMemo(() => ({ onErrorToast }), [onErrorToast]);

  return (
    <ExperimentalDesignSystemContextProvider value={value}>
      <ToastAnalyticsContextProvider value={toastAnalyticsValue}>
        {children}
      </ToastAnalyticsContextProvider>
    </ExperimentalDesignSystemContextProvider>
  );
}
